import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../supabaseClient";

const BG = "#FFFFFF";
const BORDER = "#DDD0C0";
const GOLD = "#C09A5B";
const TAUPE = "#8B7361";
const MUTED = "rgba(139, 115, 97, 0.75)";

const FF_PLAYFAIR = "'Playfair Display', Georgia, serif";
const FF_DM = "'DM Sans', system-ui, sans-serif";

const DOT = {
  jalon: GOLD,
  document: TAUPE,
  paiement: "#1F7A4A",
  message: "#2F6FED",
  contrat: "#D97B2E",
};

/** Mappe le champ `type` DB vers une catégorie visuelle */
function visualType(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("jalon") || t.includes("milestone") || t.includes("etape")) return "jalon";
  if (t.includes("paiement") || t.includes("payment") || t.includes("ack_payment") || t.includes("finance")) return "paiement";
  if (t.includes("message") || t.includes("messager")) return "message";
  if (t.includes("contrat") || t.includes("contract") || t.includes("signature")) return "contrat";
  if (t.includes("document") || t.includes("rapport") || t.includes("pv") || t.includes("fichier")) return "document";
  return "document";
}

function formatRelativeFr(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const now = Date.now();
  const sec = Math.floor((now - d.getTime()) / 1000);
  if (sec < 45) return "À l’instant";
  if (sec < 3600) return `Il y a ${Math.floor(sec / 60)} min`;
  if (sec < 86400) return `Il y a ${Math.floor(sec / 3600)} h`;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday - startOfDay) / 86400000);
  if (diffDays === 1) return "Hier";
  if (diffDays > 1 && diffDays < 7) return `Il y a ${diffDays} jours`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined });
}

function BellIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M6 8a6 6 0 1 1 12 0c0 1.78.85 3.46 2.28 4.52l.45.33a1.5 1.5 0 0 1-.88 2.72H4.15a1.5 1.5 0 0 1-.88-2.72l.45-.33A6.02 6.02 0 0 0 6 8Z"
        stroke="#8B7361"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10.3 19a2.7 2.7 0 0 0 3.4 0" stroke="#8B7361" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Cloche de notifications temps réel (Supabase).
 * @param {{ id: string, email?: string }} currentUser
 */
export default function NotificationBell({ currentUser }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const userId = currentUser?.id;

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("destinataire_id", userId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      setItems(data || []);
    } catch (e) {
      console.error("NotificationBell load:", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setItems([]);
      return;
    }
    load();
  }, [userId, load]);

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`notifications-bell-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `destinataire_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new;
          if (!row?.id) return;
          setItems((prev) => {
            const next = [row, ...prev.filter((n) => n.id !== row.id)];
            return next.slice(0, 20);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const unreadCount = useMemo(() => items.filter((n) => !n.lu).length, [items]);

  async function markOneRead(notif) {
    if (!userId || !notif?.id || notif.lu) return;
    const { error } = await supabase.from("notifications").update({ lu: true }).eq("id", notif.id);
    if (error) {
      console.error(error);
      return;
    }
    setItems((prev) => prev.map((n) => (n.id === notif.id ? { ...n, lu: true } : n)));
  }

  async function markAllRead() {
    if (!userId || unreadCount === 0) return;
    const { error } = await supabase.from("notifications").update({ lu: true }).eq("destinataire_id", userId);
    if (error) {
      console.error(error);
      return;
    }
    setItems((prev) => prev.map((n) => ({ ...n, lu: true })));
  }

  if (!userId) return null;

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <button
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          padding: 0,
          border: `1px solid ${BORDER}`,
          borderRadius: 10,
          background: BG,
          cursor: "pointer",
          boxSizing: "border-box",
        }}
      >
        <BellIcon />
        {unreadCount > 0 ? (
          <span
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              minWidth: 18,
              height: 18,
              padding: "0 5px",
              borderRadius: 999,
              background: "#C0392B",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              fontFamily: FF_DM,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              boxShadow: "0 0 0 2px #fff",
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Liste des notifications"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 320,
            maxHeight: 420,
            background: BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            boxShadow: "0 16px 40px rgba(60, 48, 36, 0.12)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "14px 16px",
              borderBottom: `1px solid ${BORDER}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexShrink: 0,
            }}
          >
            <span style={{ fontFamily: FF_PLAYFAIR, fontSize: 16, fontWeight: 600, color: "#2C241C" }}>Notifications</span>
            <button
              type="button"
              onClick={markAllRead}
              disabled={unreadCount === 0}
              style={{
                fontFamily: FF_DM,
                fontSize: 11,
                fontWeight: 600,
                color: unreadCount === 0 ? MUTED : GOLD,
                background: "transparent",
                border: "none",
                cursor: unreadCount === 0 ? "default" : "pointer",
                textDecoration: unreadCount === 0 ? "none" : "underline",
                textUnderlineOffset: 2,
                padding: 0,
                whiteSpace: "nowrap",
              }}
            >
              Tout marquer comme lu
            </button>
          </div>

          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            {loading && items.length === 0 ? (
              <div style={{ padding: 20, fontFamily: FF_DM, fontSize: 13, color: MUTED, textAlign: "center" }}>Chargement…</div>
            ) : items.length === 0 ? (
              <div style={{ padding: 24, fontFamily: FF_DM, fontSize: 13, color: MUTED, textAlign: "center" }}>Aucune notification</div>
            ) : (
              <ul style={{ margin: 0, padding: 8, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                {items.map((n) => {
                  const cat = visualType(n.type);
                  const dot = DOT[cat] || TAUPE;
                  const unread = !n.lu;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => markOneRead(n)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          border: `1px solid ${BORDER}`,
                          borderRadius: 10,
                          padding: "10px 12px 10px 14px",
                          background: unread ? "rgba(192, 154, 91, 0.08)" : BG,
                          cursor: "pointer",
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                          boxSizing: "border-box",
                        }}
                      >
                        <span
                          aria-hidden
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: dot,
                            marginTop: 5,
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontFamily: FF_DM, fontSize: 13, fontWeight: 700, color: "#2C241C", lineHeight: 1.35 }}>
                            {n.titre || "Notification"}
                          </div>
                          {n.message ? (
                            <div style={{ fontFamily: FF_DM, fontSize: 12, color: MUTED, marginTop: 4, lineHeight: 1.45 }}>{n.message}</div>
                          ) : null}
                          <div style={{ fontFamily: FF_DM, fontSize: 11, color: MUTED, marginTop: 6 }}>{formatRelativeFr(n.created_at)}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
